import React, { useEffect, useState } from 'react';
import { Download, Smartphone, X, CheckCircle2, Sparkles, Monitor, Info, ChevronRight, HelpCircle } from 'lucide-react';

export const PWAInstallBanner: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [installedSuccess, setInstalledSuccess] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [showGuideModal, setShowGuideModal] = useState(false);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    window.addEventListener('appinstalled', () => {
      setIsInstallable(false);
      setInstalledSuccess(true);
      setTimeout(() => setInstalledSuccess(false), 5000);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const getPlatform = () => {
    const ua = navigator.userAgent || '';
    if (/android/i.test(ua)) return 'ANDROID';
    if (/iphone|ipad|ipod/i.test(ua)) return 'IOS';
    if (/mac/i.test(ua)) return 'MAC';
    return 'DESKTOP';
  };

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') {
          setInstalledSuccess(true);
        }
        setDeferredPrompt(null);
        setIsInstallable(false);
        return;
      } catch (err) {
        console.warn('[PWA] Prompt failed, showing platform guide', err);
      }
    }

    // If deferredPrompt is null or prompt failed, open platform-specific installer modal
    setShowGuideModal(true);
  };

  const handleDownloadAndroidAPK = () => {
    const link = document.createElement('a');
    link.href = '/downloads/AbilityOS_Android_Enterprise_v1.0.apk';
    link.download = 'AbilityOS_Android_Enterprise_v1.0.apk';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setShowGuideModal(false);
  };

  const platform = getPlatform();

  // Do not block screen on desktop viewports or if dismissed
  if (dismissed || platform === 'DESKTOP' || platform === 'MAC') return null;

  return (
    <>
      <div className="fixed bottom-20 left-4 right-4 sm:right-auto sm:max-w-xs z-40 p-3 bg-gradient-to-r from-slate-900 via-blue-950 to-slate-900 text-white rounded-2xl border border-blue-500/40 shadow-2xl space-y-2.5 animate-fade-in">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-500/20 border border-blue-400/40 rounded-xl flex items-center justify-center text-amber-400 shrink-0">
              <Smartphone className="w-4 h-4" />
            </div>
            <div>
              <div className="text-[11px] font-black text-white flex items-center gap-1">
                <span>Install Mobile App</span>
                <span className="px-1 py-0.2 bg-emerald-500/30 text-emerald-200 text-[8px] font-extrabold rounded">
                  PWA
                </span>
              </div>
              <div className="text-[10px] text-blue-200">1-Tap Install / APK</div>
            </div>
          </div>

          <button 
            onClick={() => setDismissed(true)}
            className="text-slate-400 hover:text-white p-0.5 rounded-lg"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        {installedSuccess ? (
          <div className="p-1.5 bg-emerald-500/20 border border-emerald-400/40 text-emerald-200 text-[10px] font-bold rounded-xl text-center flex items-center justify-center gap-1">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
            <span>Installed!</span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 pt-0.5">
            <button
              onClick={handleInstallClick}
              className="flex-1 py-1.5 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-bold rounded-xl text-[11px] shadow transition flex items-center justify-center gap-1"
            >
              <Download className="w-3.5 h-3.5" />
              <span>Install App</span>
            </button>
            <button
              onClick={() => setDismissed(true)}
              className="px-2 py-1.5 bg-white/10 hover:bg-white/20 text-slate-300 font-bold rounded-xl text-[11px]"
            >
              Later
            </button>
          </div>
        )}
      </div>

      {/* Platform-Specific Installation Modal */}
      {showGuideModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-md">
          <div className="bg-slate-900 border border-blue-500/40 text-white rounded-3xl p-6 max-w-md w-full shadow-2xl space-y-5">
            
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div className="flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-amber-400" />
                <h3 className="font-extrabold text-base">Install AbilityOS Mobile App</h3>
              </div>
              <button onClick={() => setShowGuideModal(false)} className="text-slate-400 hover:text-white font-bold">✕</button>
            </div>

            {platform === 'ANDROID' && (
              <div className="space-y-4">
                <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-2xl space-y-2">
                  <div className="text-xs font-bold text-emerald-300 flex items-center gap-1.5">
                    <Sparkles className="w-4 h-4 text-emerald-400" />
                    <span>Option 1: Direct Android APK Installer (Recommended)</span>
                  </div>
                  <p className="text-[11px] text-slate-300">
                    Download the standalone Android app installer package (.apk) for offline usage.
                  </p>
                  <button
                    onClick={handleDownloadAndroidAPK}
                    className="w-full py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-xs rounded-xl shadow transition flex items-center justify-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    <span>Download Android APK App</span>
                  </button>
                </div>

                <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-2xl space-y-2">
                  <div className="text-xs font-bold text-blue-300 flex items-center gap-1.5">
                    <Info className="w-4 h-4 text-blue-400" />
                    <span>Option 2: Add PWA to Android Home Screen</span>
                  </div>
                  <ol className="text-[11px] text-slate-300 space-y-1 list-decimal list-inside">
                    <li>Tap the Chrome menu button (<strong>⋮</strong> three dots at top right).</li>
                    <li>Select <strong>"Install app"</strong> or <strong>"Add to Home screen"</strong>.</li>
                    <li>Confirm by tapping <strong>"Add"</strong>.</li>
                  </ol>
                </div>
              </div>
            )}

            {platform === 'IOS' && (
              <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-2xl space-y-3">
                <div className="text-xs font-bold text-amber-300 flex items-center gap-1.5">
                  <Info className="w-4 h-4 text-amber-400" />
                  <span>iOS Safari PWA Installation Instructions</span>
                </div>
                <ol className="text-xs text-slate-200 space-y-2 list-decimal list-inside">
                  <li>Tap the <strong>Share</strong> icon (square with arrow up ⎕↑ at Safari toolbar).</li>
                  <li>Scroll down and tap <strong>"Add to Home Screen"</strong>.</li>
                  <li>Tap <strong>"Add"</strong> in the top right corner.</li>
                </ol>
              </div>
            )}

            {platform !== 'ANDROID' && platform !== 'IOS' && (
              <div className="p-4 bg-slate-800 border border-slate-700 rounded-2xl space-y-3">
                <div className="text-xs font-bold text-blue-300 flex items-center gap-1.5">
                  <Monitor className="w-4 h-4 text-blue-400" />
                  <span>Desktop Chrome / Edge PWA Installation</span>
                </div>
                <p className="text-xs text-slate-300">
                  Click the <strong>Install Icon (⊕)</strong> located on the right side of your browser address bar to install AbilityOS on your desktop computer.
                </p>
              </div>
            )}

            <button
              onClick={() => setShowGuideModal(false)}
              className="w-full py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold text-xs rounded-xl"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
};
